import React from 'react';
import {
    getAiWardenCitationTargetId,
    formatCitationRefBracket,
    getCitationDisplayTitle,
    getCitationRefClassName,
    isIrlCitation,
    isObservationCitation,
} from '../../utils/citationDisplay';
import styles from './css/index.module.css';

/**
 * Single citation card in AiWarden lists (VDR opens PDF; IRL/observation open read-only sidebar).
 */
const AiWardenCitationListItem = ({
    citation,
    linkedChunk,
    citationDisplayCtx,
    scope,
    canOpenVdr,
    canOpenIrl,
    canOpenObservation,
    onVdrOpen,
    onIrlOpen,
    onObservationOpen,
    getCitationSimilarityLabel,
    normalizeChunkTopics,
    takeTopChunkKeywords,
}) => {
    const tooltipText = (linkedChunk?.chunk || '').trim();
    const topKw = takeTopChunkKeywords(citation, linkedChunk, 5);
    const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
    const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
    const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
    const isObs = isObservationCitation(citation, linkedChunk);
    const isIrl = !isObs && isIrlCitation(citation, linkedChunk);
    const canOpen = isObs ? canOpenObservation : isIrl ? canOpenIrl : canOpenVdr;

    const handleOpen = () => {
        if (isObs) onObservationOpen?.(citation, linkedChunk);
        else if (isIrl) onIrlOpen?.(citation, linkedChunk);
        else onVdrOpen?.(citation, linkedChunk);
    };

    const targetId = getAiWardenCitationTargetId(scope, citation);
    const refClass = getCitationRefClassName(styles, citation, linkedChunk);
    const cardKindClass = isObs
        ? styles.aiWardenCitationCardObservation
        : isIrl
          ? styles.aiWardenCitationCardIrl
          : '';

    return (
        <li
            id={targetId}
            className={`${styles.aiWardenCitationCard}${canOpen ? ` ${styles.aiWardenCitationCardInteractive}` : ''}${cardKindClass && (canOpen || isObs) ? ` ${cardKindClass}` : ''}`}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : undefined}
            onClick={() => canOpen && handleOpen()}
            onKeyDown={e => {
                if (!canOpen) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                }
            }}>
            <span className={refClass}>{formatCitationRefBracket(citation.ref)}</span>
            <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(citation, linkedChunk, citationDisplayCtx)}</span>
            {getCitationSimilarityLabel(citation) && (
                <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(citation)}</span>
            )}
            {(chunkDocType || chunkIntent) && (
                <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                    {chunkDocType && <p>Type: {chunkDocType}</p>}
                    {chunkIntent && <p>Intent: {chunkIntent}</p>}
                </div>
            )}
            {chunkTopics.length > 0 && (
                <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                    {chunkTopics.map(topic => (
                        <span key={`${citation.ref}-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                            {topic}
                        </span>
                    ))}
                </div>
            )}
            {topKw.length > 0 && (
                <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                    {topKw.map(kw => (
                        <span key={`${citation.ref}-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                            {kw}
                        </span>
                    ))}
                </div>
            )}
            {tooltipText && (
                <span className={styles.aiWardenCitationTooltip} role="tooltip">
                    {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                </span>
            )}
        </li>
    );
};

export default AiWardenCitationListItem;
